import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaClipboardList,
  FaSpinner,
  FaSync,
  FaGraduationCap,
  FaClock,
  FaCheckCircle,
  FaTrash,
  FaUsers,
} from "react-icons/fa";
import PageHeader from "../../../core/common/header/PageHeader";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type BatchAssignment = {
  id: number;
  examSetId: number;
  examSetName: string;
  level: string;
  batchId: number;
  batchName: string;
  dueDate: string;
  assignedAt: string;
  studentCount: number;
  submittedCount: number;
  gradedCount: number;
  publishedCount: number;
};

const AdminExamAssignments: React.FC = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<BatchAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/exams/assignments`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const result = await response.json();
        setAssignments(result.data || result);
        setLastUpdated(new Date());
      } else {
        setError("Failed to fetch assignments");
      }
    } catch (err) {
      console.error("Error fetching assignments:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchAssignments();
    setRefreshing(false);
  };

  const handleDeleteAssignment = async (assignmentId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this assignment? This will remove it for all students in the batch."
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/exams/assignments/${assignmentId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (response.ok) {
        setAssignments(
          assignments.filter((assignment) => assignment.id !== assignmentId)
        );
      } else {
        const data = await response.json();
        alert(data.error || "Failed to delete assignment");
      }
    } catch (err) {
      console.error("Error deleting assignment:", err);
      alert("Network error. Please try again.");
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (assignment: BatchAssignment) => {
    if (
      assignment.publishedCount === assignment.studentCount &&
      assignment.studentCount > 0
    ) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-800">
          <FaCheckCircle size={10} />
          All Published
        </span>
      );
    } else if (assignment.gradedCount > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
          <FaClipboardList size={10} />
          Grading
        </span>
      );
    } else if (assignment.submittedCount > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold bg-orange-100 text-orange-800">
          <FaClock size={10} />
          Needs Grading
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold bg-yellow-100 text-yellow-800">
          <FaClock size={10} />
          Scheduled
        </span>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaGraduationCap />}
        title="Exam Assignments & Grading"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Exam Management", to: "/admin/exam-management" },
          { label: "Assignments" },
        ]}
      />

      {/* Toolbar */}
      <div className="flex items-center justify-end gap-3 max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <span className="text-xs text-gray-500">
          Updated:{" "}
          {lastUpdated.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
        <button
          onClick={refreshData}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 text-sm font-semibold shadow-sm"
        >
          <FaSync className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md">
            <p className="font-semibold">Error</p>
            <p>{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center text-[#009fe3] font-semibold py-20 text-xl flex items-center justify-center gap-2">
            <FaSpinner className="animate-spin" />
            Loading assignments...
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                      Batch
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                      Exam
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                      Progress
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {assignments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-10 text-center text-gray-400 font-semibold"
                      >
                        <FaClipboardList className="mx-auto h-12 w-12 text-gray-300 mb-2" />
                        <p>No batch assignments found.</p>
                        <p className="text-sm mt-2">
                          Assign exams to batches from the Exam Management page.
                        </p>
                      </td>
                    </tr>
                  ) : (
                    assignments.map((assignment, index) => (
                      <tr
                        key={assignment.id}
                        className={`${
                          index % 2 === 1 ? "bg-gray-50" : "bg-white"
                        } hover:bg-blue-50 transition-colors`}
                      >
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-semibold text-gray-900 flex items-center gap-2">
                              <FaUsers className="text-[#009fe3]" size={14} />
                              {assignment.batchName || "Unnamed Batch"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {assignment.studentCount} student(s)
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-semibold text-gray-900">
                              {assignment.examSetName}
                            </div>
                            <div className="mt-1">
                              <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800">
                                {assignment.level}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {getStatusBadge(assignment)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-center">
                            <div className="space-y-1">
                              <div className="text-sm">
                                <span className="font-semibold text-green-600">
                                  {assignment.submittedCount}
                                </span>
                                <span className="text-gray-500">
                                  /{assignment.studentCount}
                                </span>
                                <span className="text-gray-600 ml-1">
                                  submitted
                                </span>
                              </div>
                              <div className="text-xs text-gray-600">
                                {assignment.gradedCount} graded •{" "}
                                {assignment.publishedCount} published
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-700">
                          {formatDate(assignment.dueDate)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() =>
                                navigate(
                                  `/admin/exams/assignments/${assignment.id}/grade`
                                )
                              }
                              className="inline-flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-md hover:shadow-lg transition-all text-sm font-semibold"
                              title="Grade All Students in Batch"
                            >
                              <FaGraduationCap size={14} />
                              Grade Batch
                              {assignment.submittedCount >
                                assignment.gradedCount && (
                                <span className="ml-1 px-1.5 py-0.5 bg-white/30 rounded text-xs">
                                  {assignment.submittedCount -
                                    assignment.gradedCount}
                                </span>
                              )}
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteAssignment(assignment.id)
                              }
                              className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-semibold"
                              title="Delete Assignment"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminExamAssignments;
